<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script module>
  /**
   * @typedef { "small" | "medium" | "large" } MapSize
   */
  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } rawData
   * @property { Array<Record<string, any>> } [tableData]
   * @property { import("$utils/config").ColumnConfig[] } configData
   * @property { Array<Record<string, any>> } metadataData
   * @property { Array<Record<string, any>> } sourceData
   * @property { import("$lib/types").mapType } [mapType="hex"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { boolean } [showStateSummary = true]
   * @property { string } [dropdownLabel]
   * @property { string } [tablePlaceholderLabel]
   * @property { boolean } [showDropdown = false]
   * @property { boolean } [centerDropdown = false]
   * @property { MapSize } [mapSize = "medium"]
   * @property { string } [toolTitle=""]
   * @property { string } [headerText="View data for [dropdown]"]
   * @property { string } [stateDescriptionTemplate="View data for [state] below."]
   */
</script>

<script>
  import DetailHeader from "$lib/components/DetailHeader/DetailHeader.svelte";
  import SectionInfo from "$lib/components/SectionInfo/SectionInfo.svelte";
  import KeyValueSet from "$lib/components/KeyValueSet/KeyValueSet.svelte";
  import KeyValueItem from "$lib/components/KeyValueItem/KeyValueItem.svelte";
  import ChecklistSet from "$lib/components/ChecklistSet/ChecklistSet.svelte";
  import ChecklistItem from "$lib/components/ChecklistItem/ChecklistItem.svelte";
  import { Block, BasicDropdown, Theme } from "@urbaninstitute/dataviz-components";
  import windowWidth from "$lib/stores/windowWidth.js";
  import { createConfig, setConfig } from "$lib/utils/config.js";
  import { getStatesInfo, getStateName } from "$utils/states.js";
  import InfoBox from "$lib/components/InfoBox/InfoBox.svelte";
  import InfoBoxLayout from "$lib/components/InfoBoxLayout/InfoBoxLayout.svelte";
  import { formatTextTemplate } from "$utils/text.js";
  import { ToolData } from "$utils/data.js";
  import { getDisplayValue, getBooleanValue, getPrograms } from "$utils/profileData.js";
  import {
    section1Box1Cols,
    section1Box2Cols,
    section2Box3Cols,
    section3Box1Cols,
    section3Box2Cols,
    section3Box3Cols,
    section3Box4Cols,
    section4Box1Cols,
    section4Box2Cols,
    section5ProgramCols
  } from "./profileSections.js";
  import { getTotalHeaderHeight } from "$lib/utils/page-header.js";
  import { onMount } from "svelte";
  import content from "$data/profile_content.aml";

  /** @type {MapToolProps} */
  const {
    data,
    rawData,
    tableData,
    configData,
    metadataData,
    sourceData,
    mapType = "hex",
    mapSize = "medium",
    showStateSummary = true,
    accentColor = "#fdbf11",
    featureOption = "states",
    dropdownLabel = "Select a metric to view",
    tablePlaceholderLabel = "Select a state to see data",
    showDropdown = false,
    centerDropdown = false,
    toolTitle = "",
    headerText = "View data for [dropdown]",
    stateDescriptionTemplate = "View data for [state] below."
  } = $props();

  // Build a label lookup from column_metadata: column_name → label
  const columnLabelMap = new Map(metadataData.map((d) => [d.column_name, d.label]));

  /** @type {ResizeObserver | undefined} */
  let resizeObserver;

  /** @param {string} col */
  function getLabel(col) {
    return columnLabelMap.get(col) || col;
  }

  /** Shorthand: format a column value from the current raw row */
  function fmtValue(/** @type {string} */ col) {
    return getDisplayValue(currentRawRow, col, parsedConfigData);
  }

  /** Shorthand: parse a boolean column from the current raw row */
  function fmtBool(/** @type {string} */ col) {
    return getBooleanValue(currentRawRow, col);
  }

  // --- Data and config setup ---

  $effect(() => {
    if (!parsedData) {
      parsedData = new ToolData(data, tableData);
    }
    if (!parsedConfigData) {
      parsedConfigData = createConfig(configData, accentColor);
    }
  });

  let currentState = $state();
  let currentMetric = $state();

  // track page header for sticky elements
  let pageHeaderHeight = $state(0);

  $effect(() => {
    if (parsedData && !currentMetric) {
      currentMetric = parsedData.getPrimaryColumns()[0];
    }
  });

  /** @type {ToolData | undefined} */
  let parsedData = $state();
  let parsedConfigData = $state();

  setConfig(() => parsedConfigData);

  // Build dropdown options only from states present in the data
  const dataStateIds = new Set(rawData.map((d) => d.geoid));
  // svelte-ignore state_referenced_locally
  const stateDropdownData = getStatesInfo(featureOption)
    .filter(({ fips }) => dataStateIds.has(fips))
    .map(({ fips, state }) => ({
      value: fips,
      label: state
    }));

  // Get the raw row for the selected state (preserves strings and booleans)
  const currentRawRow = $derived(
    currentState ? rawData.find((d) => d.geoid === currentState) : undefined
  );

  const currentStateName = $derived(currentState ? getStateName(currentState) : undefined);

  // Template variables for formatTextTemplate
  const templateVars = $derived(
    currentStateName ? { state: currentStateName } : { state: "the selected state" }
  );

  // Programs for section 5 (filter out NA/empty)
  const currentPrograms = $derived(getPrograms(currentRawRow, section5ProgramCols));

  /**
   * @param {{fips?: string, map_id?: string} | null | undefined} props
   */
  function handleStateSelect(props) {
    if (!props) {
      currentState = null;
      return;
    }
    const { fips, map_id } = props;
    currentState = fips || map_id || null;
  }

  function watchHeader() {
    const headerEl = document.querySelector("lbj-header");
    const drupalEl = document.querySelector("#toolbar-administration");
    const targets = /** @type {Element[]} */ ([headerEl, drupalEl].filter(Boolean));

    if (targets.length === 0) {
      pageHeaderHeight = getTotalHeaderHeight();
      return;
    }

    resizeObserver = new ResizeObserver(() => {
      pageHeaderHeight = getTotalHeaderHeight();
    });

    for (const target of targets) {
      resizeObserver.observe(target);
    }

    // Set initial value
    pageHeaderHeight = getTotalHeaderHeight();
  }
  onMount(() => {
    watchHeader();
    return () => {
      resizeObserver?.disconnect();
    };
  });
</script>

<svelte:window bind:innerWidth={$windowWidth} />
<Theme>
  {#if toolTitle}
    <Block width="body"><h6 class="tool-title">{toolTitle}</h6></Block>
  {/if}
  <Block width="wide">
    {#if parsedData && parsedConfigData}
      {#if showDropdown}
        <div
          class="tool-header"
          class:center-align={centerDropdown}
          style={`--accent-color: ${accentColor};`}
        >
          {dropdownLabel}
          <BasicDropdown
            data={parsedData
              .getPrimaryColumns()
              .filter((k) => {
                const colConfig = parsedConfigData.columns.get(k);
                return colConfig?.show_on_map !== false;
              })
              .map((k) => ({ value: k, label: parsedConfigData.columns.get(k).column_label }))}
            variant="secondary-yellow"
            inlineLabel="Select a metric to view"
            id="metric-select"
            placeholder={null}
            onchange={() => {}}
            bind:value={currentMetric}
          />
        </div>
      {/if}
      <div class="state-details" style:--page-header-height="{pageHeaderHeight}px">
        <DetailHeader bind:currentState {stateDropdownData} headerText={content.selectHeaderText} />

        {#if currentState && currentRawRow}
          <!-- Section 1: Medicaid Managed Care Program Overview -->
          <section class="profile-section">
            <SectionInfo
              subhead={content.section1.subhead}
              sectionNumber="1"
              description={formatTextTemplate(content.section1.description, templateVars)}
            />
            <InfoBoxLayout>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section1.box1Title}</h6>
                <KeyValueSet>
                  {#each section1Box1Cols as col}
                    <KeyValueItem label={getLabel(col)} value={fmtValue(col)} />
                  {/each}
                </KeyValueSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section1.box2Title}</h6>
                <KeyValueSet>
                  {#each section1Box2Cols as col}
                    <KeyValueItem label={getLabel(col)} value={fmtValue(col)} />
                  {/each}
                </KeyValueSet>
              </InfoBox>
            </InfoBoxLayout>
          </section>

          <!-- Section 2: Enrollment and Population Characteristics -->
          <section class="profile-section">
            <SectionInfo
              sectionNumber="2"
              subhead={content.section2.subhead}
              description={formatTextTemplate(content.section2.description, templateVars)}
            />
            <InfoBoxLayout>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section2.box1Title}</h6>
                <KeyValueSet>
                  <KeyValueItem
                    label={getLabel("total_medicaid_managed_care_enrollment")}
                    value={fmtValue("total_medicaid_managed_care_enrollment")}
                  />
                  <KeyValueItem
                    label={getLabel("share_of_total_medicaid_enrollment")}
                    value={fmtValue("share_of_total_medicaid_enrollment")}
                  />
                </KeyValueSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section2.box2Title}</h6>
                <KeyValueSet>
                  <KeyValueItem
                    label={getLabel(
                      "percentage_of_medicaid_managed_care_enrollees_with_type_2_diabetes_diagnosis"
                    )}
                    value={fmtValue(
                      "percentage_of_medicaid_managed_care_enrollees_with_type_2_diabetes_diagnosis"
                    )}
                  />
                </KeyValueSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section2.box3Title}</h6>
                <KeyValueSet>
                  {#each section2Box3Cols as col}
                    <KeyValueItem label={getLabel(col)} value={fmtValue(col)} />
                  {/each}
                </KeyValueSet>
              </InfoBox>
            </InfoBoxLayout>
          </section>

          <!-- Section 3: Covered Benefits and Services -->
          <section class="profile-section">
            <SectionInfo
              sectionNumber="3"
              subhead={content.section3.subhead}
              description={formatTextTemplate(content.section3.description, templateVars)}
            />
            <InfoBoxLayout>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section3.box1Title}</h6>
                <ChecklistSet>
                  {#each section3Box1Cols as col}
                    <ChecklistItem label={getLabel(col)} value={fmtBool(col)} />
                  {/each}
                </ChecklistSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section3.box2Title}</h6>
                <ChecklistSet>
                  {#each section3Box2Cols as col}
                    <ChecklistItem label={getLabel(col)} value={fmtBool(col)} />
                  {/each}
                </ChecklistSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section3.box3Title}</h6>
                <ChecklistSet>
                  {#each section3Box3Cols as col}
                    <ChecklistItem label={getLabel(col)} value={fmtBool(col)} />
                  {/each}
                </ChecklistSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section3.box4Title}</h6>
                <ChecklistSet>
                  {#each section3Box4Cols as col}
                    <ChecklistItem label={getLabel(col)} value={fmtBool(col)} />
                  {/each}
                </ChecklistSet>
              </InfoBox>
            </InfoBoxLayout>
          </section>

          <!-- Section 4: Quality Measures and Performance Incentives -->
          <section class="profile-section">
            <SectionInfo
              sectionNumber="4"
              subhead={content.section4.subhead}
              description={formatTextTemplate(content.section4.description, templateVars)}
            />
            <InfoBoxLayout>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section4.box1Title}</h6>
                <ChecklistSet>
                  {#each section4Box1Cols as col}
                    <ChecklistItem label={getLabel(col)} value={fmtBool(col)} />
                  {/each}
                </ChecklistSet>
              </InfoBox>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section4.box2Title}</h6>
                <ChecklistSet>
                  {#each section4Box2Cols as col}
                    <ChecklistItem label={getLabel(col)} value={fmtBool(col)} />
                  {/each}
                </ChecklistSet>
              </InfoBox>
            </InfoBoxLayout>
          </section>

          <!-- Section 5: Diabetes-Related Initiatives Beyond Medicaid Managed Care -->
          <section class="profile-section">
            <SectionInfo
              sectionNumber="5"
              subhead={content.section5.subhead}
              description={formatTextTemplate(content.section5.description, templateVars)}
            />
            <InfoBoxLayout>
              <InfoBox span={2}>
                <h6 class="box-title">{content.section5.box1Title}</h6>
                {#if currentPrograms.length > 0}
                  <KeyValueSet>
                    {#each currentPrograms as program}
                      <KeyValueItem label={program.name} value={program.description} />
                    {/each}
                  </KeyValueSet>
                {:else}
                  <p>{content.section5.noPrograms}</p>
                {/if}
              </InfoBox>
            </InfoBoxLayout>
          </section>
        {:else}
          <p class="placeholder-text">{tablePlaceholderLabel}</p>
        {/if}
      </div>
    {/if}
  </Block>
</Theme>

<style>
  h6.tool-title {
    margin-bottom: var(--spacing-12) !important;
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    max-width: 760px;
    margin-inline: auto;
    margin-bottom: var(--spacing-8);
  }
  .tool-header.center-align {
    text-align: center;
  }
  .tool-header :global(.dropdown-parent) {
    display: inline-block;
  }
  .tool-header :global(.dropdown-container .icons svg) {
    display: block;
  }
  .tool-header :global(.dropdown-container select.dropdown-select) {
    background-color: var(--accent-color);
    border: var(--accent-color);
  }
  .tool-header :global(select) {
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header :global(select option) {
    font-size: var(--font-size-normal) !important;
  }
  .state-details {
    margin-block: 0;
    margin-inline: auto;
    max-width: var(--size-body-max-width);
  }
  .state-details.wide {
    max-width: var(--size-wide-max-width);
  }
  .profile-section {
    margin-top: var(--spacing-16);
  }
  h6.box-title {
    margin-block-start: 0;
    background: var(--color-white);
    display: inline-block;
    padding: var(--spacing-2);
    margin-bottom: var(--spacing-6);
  }
  .placeholder-text {
    text-align: center;
    color: var(--color-gray-shade-dark);
    padding: var(--spacing-12) 0;
  }
  @media (max-width: 540px) {
    .tool-header :global(select) {
      font-size: var(--font-size-xl) !important;
    }
    .tool-header {
      font-size: var(--font-size-xl) !important;
    }
  }
</style>
